import { ApolloClient, ApolloLink, from, createHttpLink, InMemoryCache } from '@apollo/client/core';
// eslint-disable-next-line
import ApolloLinkTimeout from 'apollo-link-timeout';

import { name, version } from '../../package.json';

import { introspection } from '@/graphql/generated';
import { defineApolloClient } from '@/modules/apollo';
import { getPreviewToken } from '@/modules/cms';

// Support merged query results and use of fetchMore() utility
const offsetPaginationMerger = <T = any>(existing: T[], incoming: T[], { args: { offset = 0, limit = Infinity } }) => {
  const merged = existing ? existing.slice(0) : [];

  // Insert the incoming elements in the right places, according to args.
  const end = offset + Math.min(limit, incoming.length);
  for (let i = offset; i < end; ++i)
    merged[i] = incoming[i - offset];

  return merged;
};

export default defineApolloClient((context) => {
  const { $config, route } = context;

  const headers = {};
  const previewToken = getPreviewToken(context);
  if (previewToken)
    headers['X-Preview-Token'] = previewToken;

  // Define your apollo client
  const httpLink = createHttpLink({
    uri: $config.graphqlProxyEndpoint || $config.graphqlEndpoint,
    headers,
  });

  const cmsLink = new ApolloLink((operation, forward) => {
    const currentPath = route.path;

    const headers = {
      'X-CMS-Project': name,
      'X-CMS-Project-Revision': version,
      'X-CMS-Module-Version': 'scenkonst',
      'X-CMS-Current-Path': currentPath,
    };

    operation.setContext({ headers });
    return forward(operation);
  });

  const cache = new InMemoryCache({
    possibleTypes: introspection.possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          scenkonstOtPerformances: {
            keyArgs: ['seasons', 'archived', 'limit', 'performanceTags'],
            merge: offsetPaginationMerger,
          },
          news: {
            keyArgs: ['limit'],
            merge: offsetPaginationMerger,
          },
        },
      },
    },
  });

  const timeoutLink = new ApolloLinkTimeout(10000);
  const link = from([cmsLink, timeoutLink, httpLink]);

  const apolloClient = new ApolloClient({
    link,
    cache,
    ssrMode: process.server,
  });

  return apolloClient;
});
